import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { Avatar, Feed, filterRows, type FeedFilter } from '../components/Feed';
import { Back, Bell, Expand, Pause, Play, SkipNext, Sliders, SpeakerSmall, Wave } from '../components/Icons';
import { StatusBanner } from '../components/Status';
import { EdgeHint, LangSwitch } from '../components/Voice';
import { fmt, useT } from '../lib/i18n';
import { session, useSession } from '../lib/session';
import { updateSettings, useSettings } from '../lib/settings';
import { unlockAudio } from '../lib/sfx';

const compact = (n: number) => new Intl.NumberFormat('en', { notation: n >= 10_000 ? 'compact' : 'standard', maximumFractionDigits: 1 }).format(n);

export function NowReading({ big = false }: { big?: boolean }) {
  const t = useT();
  const s = useSession();
  const st = useSettings();
  const sp = s.speech;
  const current = sp.current;
  const paused = sp.paused;
  const label = !st.voiceOn ? t.voiceOff : paused ? t.paused : current ? t.readingAloud : t.waiting;
  const idle = !current || !st.voiceOn;
  return (
    <section aria-live="polite" className={`now${idle ? ' now--idle' : ''}${paused ? ' now--paused' : ''}`}>
      <div className="now__top">
        <span className="now__label">
          <Wave paused={idle || paused} big={big} />
          {label}
        </span>
        <span className="now__queue">{fmt(t.inQueue, { n: sp.queue.length })}</span>
      </div>
      <LangSwitch current={current} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {current && <span className="now__user">{current.user.nickname || current.user.uniqueId}</span>}
        <span className={`now__text${current ? '' : ' now__text--empty'}`}>{current ? current.text : t.nothingYet}</span>
      </div>
      <div className="now__actions">
        <button type="button" className="btn btn--green" onClick={() => (paused ? session.tts.resume() : session.tts.pause())}>
          {paused ? <Play /> : <Pause />}
          {paused ? t.resume : t.pause}
        </button>
        <button type="button" className="btn btn--green" onClick={() => session.tts.skip()}>
          <SkipNext />
          {t.skip}
        </button>
      </div>
    </section>
  );
}

const PAGE_KIND = 'live';

export function Live() {
  const t = useT();
  const s = useSession();
  const st = useSettings();
  const nav = useNavigate();
  const { username = '' } = useParams();
  const [filter, setFilter] = useState<FeedFilter>('all');

  useEffect(() => {
    if (!username) {
      nav('/');
      return;
    }
    unlockAudio();
    session.start(username, { page: PAGE_KIND });
  }, [username, nav]);

  const rows = filterRows(s.feed, filter);
  const host = s.host ?? { uniqueId: username, nickname: '@' + username };
  const live = s.status === 'live';
  const tabs: Array<[FeedFilter, string]> = [
    ['all', t.all],
    ['chat', t.chats],
    ['gift', t.giftsLikes],
    ['follow', t.followsTab],
  ];

  return (
    <div className="screen screen--live">
      <header className="live__head">
        <Link to="/" className="iconbtn" aria-label={t.stopAndBack} onClick={() => session.stop()}>
          <Back />
        </Link>
        <Avatar user={host} size="lg" />
        <div className="live__who">
          <div className="live__name">@{username}</div>
          <div className="live__meta">
            {live && <span className="tag tag--live">{t.live}</span>}
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <span className={`dot${live ? '' : s.status === 'connecting' ? ' dot--warn' : ' dot--off'}`} />
              {live ? t.connected : s.status === 'connecting' ? t.connecting : s.status === 'ended' ? t.ended : s.status === 'offline' ? t.offline : s.status === 'restricted' ? t.restricted : ''}
            </span>
          </div>
        </div>
        <Link to={`/reader/${username}`} className="iconbtn iconbtn--filled" aria-label={t.bigText}>
          <Expand />
        </Link>
        <Link to="/settings" className="iconbtn iconbtn--filled" aria-label={t.settings}>
          <Sliders />
        </Link>
      </header>

      <div className="stats">
        <div className="stat">
          <span>{t.viewers}</span>
          <strong>{compact(s.stats.viewers)}</strong>
        </div>
        <div className="stat">
          <span>{t.likes}</span>
          <strong>{compact(s.stats.likes)}</strong>
        </div>
        <div className="stat">
          <span>{t.gifts}</span>
          <strong>{compact(s.stats.gifts)}</strong>
        </div>
        <div className="stat">
          <span>{t.follows}</span>
          <strong>{compact(s.stats.follows)}</strong>
        </div>
      </div>

      <StatusBanner s={s} t={t} />
      <EdgeHint dismissible />
      <NowReading />

      <nav className="tabs" aria-label="Filter events">
        {tabs.map(([key, label]) => (
          <button key={key} type="button" className={`chip chip--sm${filter === key ? ' chip--on' : ''}`} aria-pressed={filter === key} onClick={() => setFilter(key)}>
            {label}
          </button>
        ))}
      </nav>

      <Feed rows={rows} t={t} fontSize={st.fontSize} currentId={s.speech.current?.id ?? null} empty={t.nothingYet} />

      <div className="bottombar">
        <button type="button" className="is-voice" aria-pressed={st.voiceOn} onClick={() => updateSettings({ voiceOn: !st.voiceOn })}>
          <SpeakerSmall />
          {st.voiceOn ? t.voiceOn : t.voiceOffShort}
        </button>
        <button type="button" className="is-sfx" aria-pressed={st.sfxOn} onClick={() => updateSettings({ sfxOn: !st.sfxOn })}>
          <Bell />
          {st.sfxOn ? t.soundsOn : t.soundsOff}
        </button>
        <button type="button" aria-label={t.smaller} onClick={() => updateSettings({ fontSize: Math.max(14, st.fontSize - 2) })}>
          <span className="small">A−</span>
          {t.smaller.split(' ')[0]}
        </button>
        <button type="button" aria-label={t.bigger} onClick={() => updateSettings({ fontSize: Math.min(28, st.fontSize + 2) })}>
          <span className="big">A+</span>
          {t.bigger.split(' ')[0]}
        </button>
      </div>
    </div>
  );
}
